import React, { useState, useEffect } from 'react';
import {
  Upload, Loader, CheckCircle, AlertCircle, Download, BarChart3,
  FileText, Send, Sparkles, Zap, ChevronDown, ChevronUp, Shield, ExternalLink,
  Scale
} from 'lucide-react';
import PageHeader from './PageHeader';
import './CaseSummarization.css';
import './AnalysisLayout.css';

const FYP2_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

// ─── Circular compression meter ──────────────────────────────────────────────
function CircularProgress({ percentage }) {
  const r = 60, circ = 2 * Math.PI * r;
  const offset = circ - (percentage / 100) * circ;
  const color = percentage >= 75 ? '#2ECC71' : percentage >= 50 ? '#F4C542' : '#1A73E8';
  return (
    <div className="circular-progress">
      <svg width="160" height="160" viewBox="0 0 160 160">
        <circle cx="80" cy="80" r={r} fill="none" stroke="#E3F2FD" strokeWidth="12" />
        <circle cx="80" cy="80" r={r} fill="none" stroke={color} strokeWidth="12"
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round" className="progress-ring" />
      </svg>
      <div className="progress-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
        <span className="progress-value" style={{ fontSize: '32px', fontWeight: '800', lineHeight: '1.1', color: '#1A237E' }}>{Math.round(percentage)}%</span>
        <span className="progress-label" style={{ fontSize: '12px', fontWeight: '600', color: '#64748B', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Compressed</span>
      </div>
    </div>
  );
}

// ─── Bar chart ────────────────────────────────────────────────────────────────
function BarChart({ docLen, sumLen }) {
  const max = Math.max(docLen, sumLen) * 1.2 || 1;
  return (
    <div className="bar-chart-glass">
      <h3>Document Compression</h3>
      <div className="bars-container">
        {[
          { label: 'Original', val: docLen, color: 'linear-gradient(135deg,#1A73E8,#3949AB)' },
          { label: 'Summary', val: sumLen, color: 'linear-gradient(135deg,#2ECC71,#27AE60)' },
        ].map(({ label, val, color }) => (
          <div key={label} className="bar-item">
            <div className="bar-label">{label}</div>
            <div className="bar-background">
              <div className="bar-fill" style={{ width: `${(val / max) * 100}%`, background: color }} />
            </div>
            <div className="bar-value">{val.toLocaleString()} chars</div>
          </div>
        ))}
      </div>
      <div className="chart-stat">
        Compression: {docLen > 0 ? ((1 - sumLen / docLen) * 100).toFixed(1) : 0}%
      </div>
    </div>
  );
}

// ─── Staggered Reveal Animation ──────────────────────────────────────────────
function StaggeredReveal({ text }) {
  const paragraphs = String(text).split('\n').filter(p => p.trim());
  return (
    <div className="reveal-container">
      {paragraphs.map((p, i) => (
        <p key={i} className="reveal-p" style={{ animationDelay: `${i * 0.1}s` }}>
          {p}
        </p>
      ))}
    </div>
  );
}



export default function CaseSummarization({ onNavigate }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stageIdx, setStageIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [typed, setTyped] = useState(false);

  const STAGES = [
    'Initializing Analysis',
    'Parsing PDF Content',
    'Extracting Key Sections',
    'Generating Summary',
    'Finalizing Result'
  ];

  const pickFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.pdf')) {
      setError('Only PDF files are supported.'); return;
    }
    if (f.size > 10 * 1024 * 1024) { setError('Max file size is 10 MB.'); return; }
    setFile(f); setError(null); setResult(null);
  };

  const handleSummarize = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    setStageIdx(0);
    setProgress(5);
    setStatusMessage('Starting analysis...');

    try {
      const fd = new FormData();
      fd.append('file', file);

      const initRes = await fetch(`${FYP2_BASE}/api/v1/summarize/upload`, { method: 'POST', body: fd });
      if (!initRes.ok) {
        const err = await initRes.json().catch(() => ({}));
        throw new Error(err.error || `Server error ${initRes.status}`);
      }

      const { task_id } = await initRes.json();

      const poll = async () => {
        try {
          const statusRes = await fetch(`${FYP2_BASE}/api/v1/analyze/status/${task_id}`);
          if (!statusRes.ok) return;

          const data = await statusRes.json();
          if (data.status === 'completed') {
            setLoading(false);
            const summary = data.result?.summary || {};
            const finalResult = {
              fileName: file.name,
              executiveSummary: summary.executive_summary || 'No summary available.',
              keyPoints: summary.key_points || [],
              docLength: summary.document_length || 0,
              sumLength: summary.summary_length || 0,
              chunkCount: summary.chunk_count || 0,
              analysisId: data.result?.analysis_id,
              inferenceMs: data.result?.metadata?.inference_time_ms || 0,
            };

            setResult(finalResult);
            setFile(null);
            setTyped(true);
          } else if (data.status === 'failed') {
            throw new Error(data.error || 'Summarization failed');
          } else {
            setProgress(data.progress || 10);
            setStageIdx(data.stage || 0);
            setStatusMessage(data.message || STAGES[data.stage] || 'Summarizing...');
            setTimeout(poll, 1500);
          }
        } catch (err) {
          setLoading(false);
          setError(err.message);
        }
      };

      setTimeout(poll, 1000);

    } catch (err) {
      setLoading(false);
      setError(err.message || 'Could not reach FYP-2 backend. Make sure it is running on port 5001.');
    }
  };

  const handleDownload = () => {
    if (!result) return;
    const txt = [
      'CASE SUMMARY REPORT',
      '==========================================',
      `File      : ${result.fileName}`,
      `Generated : ${new Date().toLocaleString()}`,
      `ID        : ${result.analysisId}`,
      '',
      'EXECUTIVE SUMMARY',
      '-'.repeat(40),
      result.executiveSummary,
      '',
      'KEY POINTS',
      '-'.repeat(40),
      ...result.keyPoints.map((p, i) => `${i + 1}. ${p}`),
      '',
      `Chunks processed : ${result.chunkCount}`,
      `Processing time  : ${result.inferenceMs}ms`,
      '==========================================',
    ].join('\n');
    const url = URL.createObjectURL(new Blob([txt], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `summary-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const compression = result && result.docLength > 0
    ? Math.max(0, (1 - result.sumLength / result.docLength) * 100) : 0;

  return (
    <div className="analysis-page-container">
      <div className="analysis-hero">
        <div className="hero-text-content">
          <h1>Legal Case Summarizer</h1>
          <p>
            Get instant executive summaries highlighting key obligations, termination clauses,
            and financial commitments from complex legal documents.
          </p>
        </div>
        <div className="hero-abstract-decoration">
          <div className="abstract-circle abstract-circle-1"></div>
          <div className="abstract-circle abstract-circle-2"></div>
          <Scale className="hero-bg-icon" size={160} />
        </div>
      </div>

      <div className="analysis-grid">
        <div className="analysis-main-col">
          {!result ? (
            <>
              <div className={`upload-card-premium ${file ? 'has-file' : ''}`}>
                <div className="upload-icon-wrapper">
                  <Upload size={32} />
                </div>
                <h3>Drag and drop your case files here</h3>
                <p>or browse to find a file on your computer</p>

                <div className="upload-actions">
                  <input type="file" id="sum-upload" accept=".pdf" style={{ display: 'none' }}
                    onChange={e => pickFile(e.target.files[0])} />
                  <button className="btn-premium primary" onClick={() => document.getElementById('sum-upload').click()}>
                    Select Files
                  </button>
                </div>

                {file && (
                  <div className="file-preview-chip" style={{ marginBottom: 24 }}>
                    <FileText size={20} />
                    <div className="file-info">
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">{(file.size / 1024).toFixed(1)} KB</span>
                    </div>
                    <button className="remove-file" onClick={() => setFile(null)}>×</button>
                  </div>
                )}


                {loading && (
                  <div className="loading-overlay-glass">
                    <div className="processing-visual">
                      <div className="spinner-ring"></div>
                      <div className="spinner-ring inner"></div>
                      <div className="processing-icon-center">
                        <FileText size={40} />
                      </div>
                    </div>
                    <div className="processing-status-text">{statusMessage || STAGES[stageIdx]}</div>
                    <div className="processing-subtext">Optimizing analysis for legal accuracy</div>

                    <div className="progress-container-modern">
                      <div
                        className="progress-fill-modern"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>


              {!loading && file && !result && (
                <div style={{ marginTop: '32px' }}>
                  <button className="btn-premium primary" style={{ width: '100%', padding: '18px', fontSize: '16px', borderRadius: '16px' }} onClick={handleSummarize}>
                    <Zap size={20} style={{ marginRight: '8px' }} />
                    Generate AI Summary
                  </button>
                </div>
              )}

              {!loading && !result && (
                <div className="capabilities-section" style={{ marginTop: '40px' }}>
                  <div className="section-divider" style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                    <div style={{ height: '1px', flex: 1, background: 'linear-gradient(to right, transparent, #e2e8f0)' }}></div>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Summarization Capabilities</span>
                    <div style={{ height: '1px', flex: 1, background: 'linear-gradient(to left, transparent, #e2e8f0)' }}></div>
                  </div>

                  <div className="capabilities-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="cap-card" style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #f1f5f9', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                      <div style={{ color: 'var(--color-primary)', marginBottom: '12px' }}><Sparkles size={24} /></div>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '15px' }}>Executive Summarization</h4>
                      <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>Condenses hundred-page legal filings into 2-3 paragraphs of critical executive insights.</p>
                    </div>
                    <div className="cap-card" style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #f1f5f9', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                      <div style={{ color: '#27ae60', marginBottom: '12px' }}><Zap size={24} /></div>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '15px' }}>Key Point Extraction</h4>
                      <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>Automatically identifies and bullet-points obligations, deadlines, and financial terms.</p>
                    </div>
                    <div className="cap-card" style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #f1f5f9', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                      <div style={{ color: '#f39c12', marginBottom: '12px' }}><FileText size={24} /></div>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '15px' }}>Multi-Chunk Processing</h4>
                      <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>Handles massive documents by intelligently chunking and synthesizing context across sections.</p>
                    </div>
                    <div className="cap-card" style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #f1f5f9', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                      <div style={{ color: '#9b59b6', marginBottom: '12px' }}><Shield size={24} /></div>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '15px' }}>Objective Analysis</h4>
                      <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>Provides a neutral, fact-based overview of legal standing without human bias.</p>
                    </div>
                  </div>
                </div>
              )}

            </>
          ) : (
            <div className="analysis-result-view">
              <PageHeader
                title={result.fileName}
                subtitle={`Summary generated on ${new Date().toLocaleDateString()}`}
                onBack={() => setResult(null)}
                actionButton={
                  <button className="btn-premium secondary" onClick={handleDownload}>
                    <Download size={18} /> Download Summary
                  </button>
                }
              />

              <div style={{ marginTop: 24 }}>
                <div className="tabs-header-modern" style={{ marginBottom: 24 }}>
                  <button className={`tab-btn ${activeTab === 'summary' ? 'active' : ''}`} onClick={() => setActiveTab('summary')}>
                    Executive Summary
                  </button>
                  <button className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`} onClick={() => setActiveTab('details')}>
                    Key Points
                  </button>
                </div>

                {activeTab === 'summary' && (
                  <div className="summary-card-glass" style={{ background: 'var(--glass-white)', padding: '24px', borderRadius: '16px', border: '1px solid var(--glass-border)' }}>
                    <p style={{ lineHeight: 1.7, color: 'var(--color-text-primary)', fontSize: 15 }}>
                      {typed ? <StaggeredReveal text={result.executiveSummary} /> : result.executiveSummary}
                    </p>
                  </div>
                )}

                {activeTab === 'details' && (
                  <div className="details-grid" style={{ display: 'grid', gap: 16 }}>
                    {result.keyPoints.map((pt, i) => (
                      <div key={i} className="detail-card-glass" style={{ background: 'var(--glass-white)', padding: '16px', borderRadius: '12px', border: '1px solid var(--glass-border)', display: 'flex', gap: 16 }}>
                        <div style={{ background: 'var(--color-primary)', color: 'white', width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 12, fontWeight: 'bold' }}>{i + 1}</div>
                        <p style={{ margin: 0, fontSize: 14 }}>{pt}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="analysis-sidebar-col">
          <div className="sidebar-info-card">
            <div className="card-body-content">
              <div className="card-icon-header"><FileText size={20} /></div>
              <h4>Case Summaries</h4>
              <p>Get instant executive summaries highlighting key obligations, termination clauses, and financial commitments.</p>
              <button
                className="btn-premium secondary"
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px' }}
                onClick={() => window.open('/guide.html', '_blank')}
              >
                Learn More <ExternalLink size={16} />
              </button>
            </div>
          </div>

          {result && (
            <div className="sidebar-info-card">
              <div className="card-icon-header"><BarChart3 size={20} /></div>
              <h4>Efficiency Stats</h4>
              <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
                <CircularProgress percentage={Math.round(compression)} />
              </div>
              <div className="feature-list">
                <div className="analysis-feature-item">
                  <span>Original</span>
                  <span>{result.docLength.toLocaleString()} chars</span>
                </div>
                <div className="analysis-feature-item">
                  <span>Summary</span>
                  <span>{result.sumLength.toLocaleString()} chars</span>
                </div>
              </div>
            </div>
          )}

          {/* System Status Removed */}
        </div>
      </div>
    </div>
  );
}
